import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { FadeUp, EASE } from "./Reveal";
import { GALLERY_IMAGES } from "@/data/apartments";
import { useLang } from "@/i18n";

const SpotImage = ({ img, cap, i, onOpen }) => (
  <motion.figure
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay: i * 0.07, ease: EASE }}
    onClick={() => onOpen(i)}
    className={`group relative cursor-zoom-in overflow-hidden ${img.cls} ${img.arch ? "rounded-t-[999px]" : ""}`}
    data-testid={`gallery-item-${i}`}
  >
    <motion.img
      initial={{ scale: 1.08 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.4, ease: EASE }}
      src={img.src}
      alt={cap}
      loading="lazy"
      className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
    />
    <div className={`pointer-events-none absolute inset-0 ring-1 ring-inset ring-ink/10 ${img.arch ? "rounded-t-[999px]" : ""}`} />
    <figcaption className="absolute bottom-4 left-4 bg-ivory/90 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink backdrop-blur">
      {cap}
    </figcaption>
  </motion.figure>
);

const Lightbox = ({ index, captions, onClose, onNav }) => {
  const img = GALLERY_IMAGES[index];
  const [touchX, setTouchX] = useState(null);

  useEffect(() => {
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [onClose, onNav]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[200] flex flex-col bg-ink/95 backdrop-blur-md"
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <div className="flex items-center justify-between px-6 py-5 sm:px-10">
        <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-ivory/60" data-testid="lightbox-counter">
          {index + 1} / {GALLERY_IMAGES.length}
        </span>
        <button
          onClick={onClose}
          aria-label="Chiudi"
          data-testid="lightbox-close"
          className="text-ivory/70 transition-colors hover:text-ivory"
        >
          <X className="h-7 w-7" strokeWidth={1.25} />
        </button>
      </div>
      <div
        className="relative flex flex-1 items-center justify-center px-4 pb-4 sm:px-20"
        onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX === null) return;
          const dx = e.changedTouches[0].clientX - touchX;
          if (Math.abs(dx) > 50) onNav(dx < 0 ? 1 : -1);
          setTouchX(null);
        }}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={img.src}
            initial={{ opacity: 0, scale: 0.985 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            src={img.src}
            alt={captions[index]}
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full object-contain"
            data-testid="lightbox-image"
          />
        </AnimatePresence>
        <button
          onClick={(e) => { e.stopPropagation(); onNav(-1); }}
          aria-label="Precedente"
          data-testid="lightbox-prev"
          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-ivory/25 p-3 text-ivory/80 transition-colors hover:bg-ivory hover:text-ink sm:left-8"
        >
          <ChevronLeft className="h-6 w-6" strokeWidth={1.25} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onNav(1); }}
          aria-label="Successiva"
          data-testid="lightbox-next"
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-ivory/25 p-3 text-ivory/80 transition-colors hover:bg-ivory hover:text-ink sm:right-8"
        >
          <ChevronRight className="h-6 w-6" strokeWidth={1.25} />
        </button>
      </div>
      <p className="pb-8 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-ivory/70" data-testid="lightbox-caption">
        {captions[index]}
      </p>
    </motion.div>
  );
};

export const Gallery = () => {
  const { t } = useLang();
  const [openIdx, setOpenIdx] = useState(null);

  const nav = useCallback(
    (dir) => setOpenIdx((i) => (i + dir + GALLERY_IMAGES.length) % GALLERY_IMAGES.length),
    []
  );

  return (
    <section id="galleria" className="bg-ivory py-28 sm:py-40" data-testid="gallery-section">
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10">
        <FadeUp>
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-terracotta">{t.gallery.label}</p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-tight tracking-tight text-ink sm:text-5xl">
            {t.gallery.h2a}
            <em className="italic text-terracotta">{t.gallery.h2em}</em>
          </h2>
        </FadeUp>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
          {GALLERY_IMAGES.map((img, i) => (
            <SpotImage key={img.src} img={img} cap={t.gallery.captions[i]} i={i} onOpen={setOpenIdx} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {openIdx !== null && (
          <Lightbox index={openIdx} captions={t.gallery.captions} onClose={() => setOpenIdx(null)} onNav={nav} />
        )}
      </AnimatePresence>
    </section>
  );
};
